// Email subscription utility functions
import { db } from '../firebaseInstance';
import { getAppId } from '../firebaseConfig';
import { doc, setDoc, getDoc, deleteDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';

const appId = getAppId();

// Subscribe with email
export const subscribeWithEmail = async (profileOwnerId, email) => {
  try {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Invalid email address');
    }
    
    // Create a document ID from email (sanitize for Firestore)
    const emailDocId = email.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    const subscriberRef = doc(db, 'artifacts', appId, 'users', profileOwnerId, 'subscribers', emailDocId);
    
    // Check if already subscribed
    // Note: This check may fail with permission error for unauthenticated users (public profiles),
    // so we catch and ignore - will proceed with create attempt
    try {
      const existing = await getDoc(subscriberRef);
      if (existing.exists()) {
        return { success: true, alreadySubscribed: true };
      }
    } catch (readError) {
      // If read fails due to permissions (expected for unauthenticated public profile visitors),
      // that's okay - we'll proceed with create. If document already exists, Firestore will handle it.
      console.log('Could not check existing subscription (expected for public profiles):', readError.message);
    }
    
    // Add subscriber
    // This will succeed even for unauthenticated users due to Firestore rules allowing create
    await setDoc(subscriberRef, {
      email: email.toLowerCase(),
      subscribedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error subscribing with email:', error);
    // Check if error is due to document already existing (race condition or duplicate)
    if (error.code === 'already-exists' || error.message?.includes('already exists')) {
      return { success: true, alreadySubscribed: true };
    }
    throw error;
  }
};

// Unsubscribe with email
export const unsubscribeWithEmail = async (profileOwnerId, email) => {
  try {
    const emailDocId = email.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const subscriberRef = doc(db, 'artifacts', appId, 'users', profileOwnerId, 'subscribers', emailDocId);
    await deleteDoc(subscriberRef);
    return { success: true };
  } catch (error) {
    console.error('Error unsubscribing with email:', error);
    throw error;
  }
};

// Check if email is subscribed
export const checkEmailSubscription = async (profileOwnerId, email) => {
  try {
    const emailDocId = email.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const subscriberRef = doc(db, 'artifacts', appId, 'users', profileOwnerId, 'subscribers', emailDocId);
    const snapshot = await getDoc(subscriberRef);
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking email subscription:', error);
    return false;
  }
};

// Get subscriber count.
// Reads the denormalized counter on the user doc (maintained by the
// onSubscriberCountChange Cloud Function trigger). Public visitors can't
// list the subscribers collection directly — only the owner can.
export const getSubscriberCount = async (profileOwnerId) => {
  try {
    const userDocRef = doc(db, 'artifacts', appId, 'users', profileOwnerId);
    const snap = await getDoc(userDocRef);
    const count = snap.exists() ? (snap.data()?.subscriberCount ?? 0) : 0;
    return typeof count === 'number' ? count : 0;
  } catch (error) {
    console.error('Error getting subscriber count:', error);
    return 0;
  }
};


// Parse CSV file content
export const parseCSVEmails = (csvContent) => {
  const lines = csvContent.split('\n');
  const emails = [];
  
  lines.forEach((line, index) => {
    // Skip header row (optional)
    if (index === 0 && line.toLowerCase().includes('email')) {
      return;
    }
    
    // Handle CSV format: could be "email" or "email,name" or quoted values
    const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const email = values[0];
    
    if (email && email.includes('@')) {
      emails.push(email);
    }
  });
  
  // Remove duplicates and empty values
  return emails
    .filter(email => email && email.trim())
    .map(email => email.toLowerCase().trim())
    .filter((email, index, self) => self.indexOf(email) === index);
};

// Parse plain text (one email per line)
export const parseTextEmails = (textContent) => {
  const lines = textContent.split('\n');
  const emails = lines
    .map(line => line.trim())
    .filter(line => line && line.includes('@'))
    .map(email => email.toLowerCase().trim());
  
  // Remove duplicates
  return [...new Set(emails)];
};

// Bulk import subscribers from array of emails
export const bulkImportSubscribers = async (profileOwnerId, emails, onProgress = null) => {
  try {
    if (!Array.isArray(emails) || emails.length === 0) {
      throw new Error('Emails must be a non-empty array');
    }

    const results = {
      success: 0,
      failed: 0,
      duplicates: 0,
      errors: []
    };

    // Process emails in batches to avoid overwhelming Firestore
    const batchSize = 50;
    for (let i = 0; i < emails.length; i += batchSize) {
      const batch = emails.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(async (email) => {
          try {
            const trimmedEmail = email.trim().toLowerCase();
            
            // Validate email
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(trimmedEmail)) {
              results.failed++;
              results.errors.push(`${trimmedEmail}: Invalid email format`);
              return;
            }

            // Try to subscribe
            const result = await subscribeWithEmail(profileOwnerId, trimmedEmail);
            
            if (result.alreadySubscribed) {
              results.duplicates++;
            } else {
              results.success++;
            }
          } catch (error) {
            results.failed++;
            results.errors.push(`${email}: ${error.message}`);
          }
        })
      );
    }

    return results;
  } catch (error) {
    console.error('Error bulk importing subscribers:', error);
    throw error;
  }
};
